import { ChangeEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTable, flexRender, RowSelectionState } from '@pankod/refine-react-table';
import { useDataProvider, useGetIdentity, useList } from '@pankod/refine-core';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    ButtonGroup,
    Checkbox,
    Flex,
    List,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    HStack,
    Text,
    NumberField,
    Tag,
    TagLabel,
    Wrap,
    WrapItem,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    InputGroup,
    InputLeftElement,
    Radio,
    Select,
    Stack,
    useToast,
} from '@pankod/refine-chakra-ui';
import { ActionMeta, Select as CustomSelect } from 'chakra-react-select';
import { IconUpload } from '@tabler/icons';
import { useForm } from 'react-hook-form';
import { Administrator, Organization, Permission } from 'api-client/types';

import { MainSection, Pagination, RhfMoneyInput, useApiSdk } from 'ui-core';

const importFields = [
    { key: 'name', label: 'Name', required: false },
    { key: 'retailPrice', label: 'Retail Price', required: true },
    { key: 'productType', label: 'Structure Type', required: false },
    { key: 'style', label: 'Style', required: false },
    { key: 'size', label: 'Size', required: false },
    { key: 'referenceId', label: 'Serial Number', required: false },
    { key: 'sidingColor', label: 'Siding Color', required: false },
    { key: 'trimColor', label: 'Trim Color', required: false },
    { key: 'roofColor', label: 'Roof Color', required: false },
    { key: 'condition', label: 'Condition', required: false },
    { key: 'company', label: 'Company / Channel', required: false },
];

const stockFilterMap: Record<string, any> = {
    'In Stock': {
        field: 'stockOnHand',
        operator: 'gte',
        value: 1,
    },
    Sold: {
        field: 'stockOnHand',
        operator: 'lte',
        value: 0,
    },
};

const DELETE_PRODUCTS_MUTATION = /* GraphQL */ `
mutation DeleteProducts($ids: [ID!]!) {
    deleteProducts(ids: $ids) {
        result
        message
    }
}
`;

const InventoryFilters = ({ setFilters }: { setFilters: any }) => {
    const [status, setStatus] = useState('In Stock');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const setFiltersRef = useRef(setFilters);
    const appliedFilterKeyRef = useRef<string>();

    const statuses = ['In Stock', 'Sold', 'All'];

    useEffect(() => {
        setFiltersRef.current = setFilters;
    }, [setFilters]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [search]);

    useEffect(() => {
        const trimmedSearch = debouncedSearch.trim();
        const filterKey = JSON.stringify({ status, search: trimmedSearch });

        if (appliedFilterKeyRef.current === filterKey) {
            return;
        }

        appliedFilterKeyRef.current = filterKey;

        setFiltersRef.current((prevFilters: any[] = []) => {
            const nextFilters = prevFilters.filter(
                (item: any) => (
                    item.field !== 'stockOnHand'
                    && item.key !== 'stockOnHand'
                    && item.field !== 'name'
                    && item.key !== 'name'
                )
            );

            const stockFilter = stockFilterMap[status];

            if (stockFilter) {
                nextFilters.push(stockFilter);
            }

            if (trimmedSearch) {
                nextFilters.push({
                    field: 'name',
                    operator: 'contains',
                    value: trimmedSearch,
                });
            }

            return nextFilters;
        });
    }, [debouncedSearch, status]);

    return (
        <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            mb={2}
            spacing={3}
        >
            <ButtonGroup variant="outline" size="sm" isAttached>
                {statuses.map((value: string) => (
                    <Button
                        key={value}
                        variant={status === value ? 'solid' : 'outline'}
                        onClick={() => setStatus(value)}
                    >
                        {value}
                    </Button>
                ))}
            </ButtonGroup>
            <InputGroup maxW={{ md: '360px' }}>
                <InputLeftElement
                    pointerEvents="none"
                    children={<SearchIcon color="gray.300" />}
                />
                <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search inventory"
                    bg="white"
                />
            </InputGroup>
        </Stack>
    );
};

type ImportStep = 'upload' | 'map' | 'result';
const defaultImportStockOnHand = 1;

type InventoryCreateFormValues = {
    product_type: string;
    retail_price: string;
    style: string;
    size: string;
    reference_id: string;
    siding_color: string;
    trim_color: string;
    roof_color: string;
    condition: 'New' | 'Used';
};

type ChannelOption = {
    label: string;
    value: string;
    organizationId: string;
};

const getCreateFormDefaultValues = (productTypes: any[] = []): InventoryCreateFormValues => ({
    product_type: productTypes[0]?.code || 'Shed',
    retail_price: '',
    style: '',
    size: '',
    reference_id: '',
    siding_color: '',
    trim_color: '',
    roof_color: '',
    condition: 'New',
});

export const InventoryList: React.FC = () => {
    const dataProvider = useDataProvider()();
    const apiClient = dataProvider.getClient();
    const { data: currentAdministrator } = useGetIdentity<Administrator>();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [importStep, setImportStep] = useState<ImportStep>('upload');
    const [isDragOver, setIsDragOver] = useState(false);
    const [isCreateConfigLoading, setIsCreateConfigLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importFileName, setImportFileName] = useState<string>();
    const [parseResult, setParseResult] = useState<any>();
    const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
    const [importResult, setImportResult] = useState<any>();
    const [createProductTypes, setCreateProductTypes] = useState<any[]>([]);
    const [hasLoadedCreateConfig, setHasLoadedCreateConfig] = useState(false);
    const [selectedCreateChannels, setSelectedCreateChannels] = useState<ChannelOption[]>([]);
    const [createChannelError, setCreateChannelError] = useState('');
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const fileInputId = useId();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cancelDeleteRef = useRef<HTMLButtonElement>(null);
    const sdk = useApiSdk();
    const toast = useToast();
    const createForm = useForm<InventoryCreateFormValues>({
        defaultValues: getCreateFormDefaultValues(),
    });
    const createErrors = createForm.formState.errors;
    const createRegister = createForm.register;
    const organizationListResult = useList<Organization>({
        resource: 'organization',
        config: {
            sort: [{ order: 'asc', field: 'name' }],
            pagination: { current: 0, pageSize: 200 },
        },
    });

    const organizationItems = organizationListResult?.data?.data || [];
    const availableCreateOrganizations = useMemo(() => {
        const roles = currentAdministrator?.user?.roles || [];
        const canAccessAllChannels = roles.some((role) =>
            role.permissions.includes(Permission.CreateChannel)
        );

        if (canAccessAllChannels) {
            return organizationItems;
        }

        const accessibleChannelIds = new Set(
            roles.reduce<string[]>((ids, role) => {
                role.channels.forEach((channel) => {
                    ids.push(channel.id);
                });
                return ids;
            }, [])
        );

        return organizationItems.filter((organization) =>
            accessibleChannelIds.has(organization.channel.id)
        );
    }, [currentAdministrator?.user?.roles, organizationItems]);

    const createChannelOptions = useMemo<ChannelOption[]>(() => {
        return availableCreateOrganizations.map((organization) => ({
            label: organization.name,
            value: organization.channel.id,
            organizationId: organization.id,
        }));
    }, [availableCreateOrganizations]);

    const columns = [
        {
            id: 'select',
            header: ({ table }: any) => (
                <Flex alignItems="center">
                    <Checkbox
                        isChecked={table.getIsAllRowsSelected()}
                        isIndeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                </Flex>
            ),
            cell: ({ row }: any) => (
                <Flex alignItems="center">
                    <Checkbox
                        isChecked={row.getIsSelected()}
                        isIndeterminate={row.getIsSomeSelected()}
                        disabled={!row.getCanSelect()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                </Flex>
            ),
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'name',
            header: 'Name',
            accessorKey: 'name',
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'serialNumber',
            header: 'Serial Number',
            accessorKey: 'attributes',
            cell: ({ getValue }: any) => {
                const attributes = getValue() || [];
                const refId = attributes.find((attr: any) => attr.code === 'reference_id');
                return refId?.value ?? '';
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'productType',
            header: 'Structure Type',
            accessorKey: 'attributes',
            cell: ({ getValue }: any) => {
                const attributes = getValue() || [];
                const productType = attributes.find((attr: any) => attr.code === 'product_type');
                return productType?.value ?? '';
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'attributes',
            header: 'Attributes',
            accessorKey: 'attributes',
            cell: ({ getValue }: any) => {
                const attributes = getValue() || [];
                const condition = attributes.find((attr: any) => attr.code === 'condition');
                const colorCodes = ['siding_color', 'trim_color', 'roof_color'];
                const colors = attributes.filter(
                    (attr: any) => colorCodes.includes(attr.code) && attr.value
                );
                return (
                    <div style={{ fontSize: '0.8rem', lineHeight: '1.6' }}>
                        {condition?.value && (
                            <div key={condition.code}>
                                <span style={{ fontWeight: 600, color: '#555' }}>{condition.name}:</span>{' '}
                                <span style={{ color: '#222' }}>{condition.value}</span>
                            </div>
                        )}
                        {colors.map((attr: any) => (
                            <div key={attr.code}>
                                <span style={{ fontWeight: 600, color: '#555' }}>{attr.name}:</span>{' '}
                                <span style={{ color: '#222' }}>{attr.value}</span>
                            </div>
                        ))}
                    </div>
                );
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'price',
            header: 'Price',
            accessorKey: 'price',
            cell: ({ getValue }: any) => (
                <NumberField
                    options={{ style: 'currency', currency: 'USD' }}
                    value={getValue()}
                />
            ),
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'stockOnHand',
            header: 'Stock',
            accessorKey: 'stockOnHand',
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'channels',
            header: 'Channels',
            accessorKey: 'channels',
            cell: ({ getValue }: any) => {
                const channels = getValue() || [];
                return (
                    <Wrap spacing={1}>
                        {channels.map((channel: string) => (
                            <WrapItem key={channel}>
                                <Tag size="sm" variant="subtle" colorScheme="blue">
                                    <TagLabel>{channel}</TagLabel>
                                </Tag>
                            </WrapItem>
                        ))}
                    </Wrap>
                );
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
    ];

    const {
        getHeaderGroups,
        getRowModel,
        refineCore: {
            tableQueryResult,
            setCurrent,
            pageCount,
            current,
            setFilters,
        },
    } = useTable({
        columns,
        state: {
            rowSelection,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        refineCoreProps: {
            resource: 'inventory',
            metaData: {
                operation: 'GET_RTO_INVENTORY',
            },
            initialSorter: [
                {
                    field: 'createdAt',
                    order: 'desc',
                },
            ],
            initialFilter: [stockFilterMap['In Stock']],
        },
    });

    const inventoryItems = tableQueryResult?.data?.data || [];
    const selectedItems = useMemo(() => {
        const items: any[] = [];

        for (const key in rowSelection) {
            if (rowSelection[key] && key in inventoryItems) {
                items.push(inventoryItems[Number(key)]);
            }
        }

        return items;
    }, [inventoryItems, rowSelection]);
    const selectedCount = selectedItems.length;

    useEffect(() => {
        if (!isCreateOpen || hasLoadedCreateConfig) {
            return;
        }

        let isActive = true;
        setIsCreateConfigLoading(true);

        void sdk.GetRtoConfig()
            .then((result) => {
                if (!isActive) {
                    return;
                }

                const nextProductTypes = result.rtoConfig?.options?.productTypes || [];
                setCreateProductTypes(nextProductTypes);
                setHasLoadedCreateConfig(true);

                if (
                    nextProductTypes.length
                    && !nextProductTypes.some(
                        (option: any) => option.code === createForm.getValues('product_type')
                    )
                ) {
                    createForm.setValue('product_type', nextProductTypes[0].code);
                }
            })
            .catch((error: any) => {
                if (!isActive) {
                    return;
                }

                toast({
                    title: 'Could not load inventory form settings',
                    description: error.message,
                    status: 'error',
                });
            })
            .finally(() => {
                if (isActive) {
                    setIsCreateConfigLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [createForm, hasLoadedCreateConfig, isCreateOpen, sdk, toast]);

    const resetCreate = () => {
        createForm.reset(getCreateFormDefaultValues(createProductTypes));
        setSelectedCreateChannels([]);
        setCreateChannelError('');
    };

    const closeCreate = () => {
        setIsCreateOpen(false);
        resetCreate();
    };

    const resetImport = () => {
        setImportStep('upload');
        setIsDragOver(false);
        setImportFileName(undefined);
        setParseResult(undefined);
        setColumnMapping({});
        setImportResult(undefined);
    };

    const closeImport = () => {
        setIsImportOpen(false);
        resetImport();
    };

    const closeDeleteDialog = () => {
        if (!isDeleting) {
            setIsDeleteDialogOpen(false);
        }
    };

    const handleCreateChannelChange = (
        options: readonly ChannelOption[] | null,
        _actionMeta: ActionMeta<ChannelOption>
    ) => {
        const nextOptions = options ? [...options] : [];
        setSelectedCreateChannels(nextOptions);

        if (nextOptions.length) {
            setCreateChannelError('');
        }
    };

    const handleCreate = createForm.handleSubmit(async (formData) => {
        if (!selectedCreateChannels.length) {
            setCreateChannelError('Select at least one channel.');
            return;
        }

        setCreateChannelError('');
        setIsCreating(true);

        try {
            const itemName = [formData.style, formData.size].filter(Boolean).join(' ');
            const result = await sdk.CreateRtoInventoryItem({
                input: {
                    channelIds: Array.from(new Set(selectedCreateChannels.map((option) => option.value))),
                    name: itemName,
                    retailPrice: Number(formData.retail_price),
                    productType: formData.product_type,
                    style: formData.style,
                    size: formData.size,
                    referenceId: formData.reference_id,
                    sidingColor: formData.siding_color,
                    trimColor: formData.trim_color,
                    roofColor: formData.roof_color,
                    condition: formData.condition,
                    stockOnHand: defaultImportStockOnHand,
                },
            });

            tableQueryResult?.refetch?.();
            toast({
                title: 'Inventory product created',
                description: `${result.createRtoInventoryItem.name} is now available in inventory.`,
                status: 'success',
            });
            closeCreate();
        } catch (error: any) {
            toast({
                title: 'Could not create inventory product',
                description: error.message,
                status: 'error',
            });
        } finally {
            setIsCreating(false);
        }
    });

    const parseImportFile = async (file?: File) => {
        if (!file) {
            return;
        }

        setIsParsing(true);
        setImportFileName(file.name);
        try {
            const result = await sdk.ParseRtoInventoryCsv({ file });
            setParseResult(result.parseRtoInventoryCsv);
            setColumnMapping(result.parseRtoInventoryCsv.suggestedMapping || {});
            setImportResult(undefined);
            setImportStep('map');
        } catch (error: any) {
            setImportFileName(undefined);
            toast({
                title: 'Could not parse inventory file',
                description: error.message,
                status: 'error',
            });
        } finally {
            setIsParsing(false);
            setIsDragOver(false);
        }
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        try {
            await parseImportFile(file);
        } finally {
            event.target.value = '';
        }
    };

    const handleDragOver = (event: any) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDrop = (event: any) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        void parseImportFile(file);
    };

    const handleMappingChange = (field: string, column: string) => {
        setColumnMapping((current) => ({
            ...current,
            [field]: column,
        }));
    };

    const handleImport = async () => {
        setIsImporting(true);
        try {
            const result = await sdk.ImportRtoInventory({
                input: {
                    rows: parseResult.rows,
                    columnMapping,
                    defaultStockOnHand: defaultImportStockOnHand,
                },
            });
            setImportResult(result.importRtoInventory);
            setImportStep('result');
            tableQueryResult?.refetch?.();
            toast({
                title: 'Inventory import complete',
                description: `${result.importRtoInventory.imported} products imported.`,
                status: result.importRtoInventory.failed ? 'warning' : 'success',
            });
        } catch (error: any) {
            toast({
                title: 'Could not import inventory',
                description: error.message,
                status: 'error',
            });
        } finally {
            setIsImporting(false);
        }
    };

    const handleDelete = async () => {
        const productIds = Array.from(
            new Set(
                selectedItems
                    .map((item: any) => item?.productId)
                    .filter(Boolean)
            )
        );

        if (!productIds.length) {
            setIsDeleteDialogOpen(false);
            return;
        }

        setIsDeleting(true);

        try {
            const result = await apiClient.request(DELETE_PRODUCTS_MUTATION, {
                ids: productIds,
            });

            const responses = result?.deleteProducts || [];
            const failedCount = responses.filter((item: any) => item?.result !== 'DELETED').length;

            tableQueryResult?.refetch?.();
            setRowSelection({});
            setIsDeleteDialogOpen(false);

            if (failedCount > 0) {
                toast({
                    title: 'Inventory delete partially completed',
                    description: `${productIds.length - failedCount} products deleted. ${failedCount} could not be deleted.`,
                    status: 'warning',
                });
            } else {
                toast({
                    title: 'Inventory deleted',
                    description: `${productIds.length} products were soft deleted.`,
                    status: 'success',
                });
            }
        } catch (error: any) {
            toast({
                title: 'Could not delete inventory',
                description: error.message,
                status: 'error',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const canImport = Boolean(
        parseResult?.rows?.length
        && columnMapping.retailPrice
        && (columnMapping.name || columnMapping.referenceId)
    );
    const canMapFields = Boolean(parseResult && !isParsing);

    const previewRow = parseResult?.rows?.[0];

    return (
        <MainSection>
            <List
                canCreate={false}
                title="Inventory"
                headerButtons={
                    <HStack spacing={3}>
                        <Button colorScheme="blue" onClick={() => setIsImportOpen(true)} variant="outline">
                            Import
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={() => {
                                resetCreate();
                                setIsCreateOpen(true);
                            }}
                        >
                            Create
                        </Button>
                    </HStack>
                }
            >
                {selectedCount > 0 ? (
                    <Flex
                        align={{ base: 'stretch', md: 'center' }}
                        direction={{ base: 'column', md: 'row' }}
                        justify="space-between"
                        gap={3}
                        mb={2}
                    >
                        <Text fontWeight="bold">{selectedCount} selected</Text>
                        <Button colorScheme="red" onClick={() => setIsDeleteDialogOpen(true)}>
                            Delete
                        </Button>
                    </Flex>
                ) : (
                    <InventoryFilters setFilters={setFilters} />
                )}
                <TableContainer whiteSpace="pre-line">
                    <Table variant="simple" colorScheme="gray">
                        <Thead>
                            {getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th
                                            key={header.id}
                                            {...(header.column.id === 'select' ? { px: 0, width: '1%' } : {})}
                                        >
                                            {!header.isPlaceholder && (
                                                <HStack spacing="2">
                                                    <Box>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </Box>
                                                </HStack>
                                            )}
                                        </Th>
                                    ))}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {getRowModel().rows.map((row) => (
                                <Tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <Td
                                            key={cell.id}
                                            {...(cell.column.id === 'select' ? { width: '1%', px: 0 } : {})}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </Td>
                                    ))}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
                <Pagination current={current} pageCount={pageCount} setCurrent={setCurrent} />
            </List>

            <AlertDialog
                isOpen={isDeleteDialogOpen}
                leastDestructiveRef={cancelDeleteRef}
                onClose={closeDeleteDialog}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete inventory
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Delete {selectedCount} selected inventory {selectedCount === 1 ? 'item' : 'items'}? This will soft delete the related products.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelDeleteRef} onClick={closeDeleteDialog} variant="ghost">
                                Cancel
                            </Button>
                            <Button colorScheme="red" ml={3} onClick={handleDelete} isLoading={isDeleting}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <Modal isOpen={isCreateOpen} onClose={closeCreate} size="2xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create Inventory Product</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={4}>
                            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4} w="100%">
                                {createProductTypes.length > 1 && (
                                    <FormControl isInvalid={!!createErrors.product_type} flex="1">
                                        <FormLabel>Structure Type</FormLabel>
                                        <Select {...createRegister('product_type', { required: 'Required.' })}>
                                            {createProductTypes.map((option: any) => (
                                                <option key={option.id} value={option.code}>{option.name}</option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>{createErrors.product_type?.message}</FormErrorMessage>
                                    </FormControl>
                                )}
                                <FormControl isInvalid={!!createErrors.retail_price} flex="1">
                                    <FormLabel>Retail price</FormLabel>
                                    <RhfMoneyInput
                                        name="retail_price"
                                        control={createForm.control}
                                        rules={{ required: 'Required.' }}
                                    />
                                    <FormErrorMessage>{createErrors.retail_price?.message}</FormErrorMessage>
                                </FormControl>
                            </Stack>
                            <FormControl isRequired isInvalid={!!createChannelError}>
                                <FormLabel>Channels</FormLabel>
                                <CustomSelect
                                    isMulti={true}
                                    value={selectedCreateChannels}
                                    onChange={handleCreateChannelChange}
                                    options={createChannelOptions}
                                    placeholder="Select one or more channels"
                                    closeMenuOnSelect={false}
                                />
                                <FormErrorMessage>{createChannelError}</FormErrorMessage>
                                {!createChannelOptions.length && (
                                    <Text fontSize="sm" color="gray.500" mt={2}>
                                        No channels are available for the current user.
                                    </Text>
                                )}
                            </FormControl>
                            <Stack direction={{ base: 'column', sm: 'row' }} w="100%" spacing={4}>
                                <FormControl isInvalid={!!createErrors.style}>
                                    <FormLabel>Style</FormLabel>
                                    <Input {...createRegister('style', { required: 'Required.' })} />
                                    <FormErrorMessage>{createErrors.style?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!createErrors.size}>
                                    <FormLabel>Size</FormLabel>
                                    <Input {...createRegister('size', { required: 'Required.' })} />
                                    <FormErrorMessage>{createErrors.size?.message}</FormErrorMessage>
                                </FormControl>
                            </Stack>
                            <FormControl isInvalid={!!createErrors.reference_id}>
                                <FormLabel>Serial Number or ID</FormLabel>
                                <Input {...createRegister('reference_id', { required: 'Required.' })} />
                                <FormErrorMessage>{createErrors.reference_id?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!createErrors.siding_color}>
                                <FormLabel>Siding Color</FormLabel>
                                <Input {...createRegister('siding_color', { required: 'Required.' })} />
                                <FormErrorMessage>{createErrors.siding_color?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!createErrors.trim_color}>
                                <FormLabel>Trim Color</FormLabel>
                                <Input {...createRegister('trim_color')} />
                                <FormErrorMessage>{createErrors.trim_color?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!createErrors.roof_color}>
                                <FormLabel>Roof Color</FormLabel>
                                <Input {...createRegister('roof_color')} />
                                <FormErrorMessage>{createErrors.roof_color?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!createErrors.condition}>
                                <FormLabel>Condition</FormLabel>
                                <HStack spacing={4}>
                                    <Radio value="New" {...createRegister('condition', { required: 'Required.' })}>
                                        New
                                    </Radio>
                                    <Radio value="Used" {...createRegister('condition', { required: 'Required.' })}>
                                        Used
                                    </Radio>
                                </HStack>
                                <FormErrorMessage>{createErrors.condition?.message}</FormErrorMessage>
                            </FormControl>
                            {isCreateConfigLoading && (
                                <Text fontSize="sm" color="gray.500">
                                    Loading product types...
                                </Text>
                            )}
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={closeCreate} variant="ghost">
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleCreate} isLoading={isCreating}>
                            Create Product
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isImportOpen} onClose={closeImport} size="5xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Import Inventory</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={5}>
                            {importStep === 'upload' && (
                                <Box
                                    borderWidth="2px"
                                    borderRadius="md"
                                    borderStyle="dashed"
                                    borderColor={isDragOver ? 'blue.500' : 'gray.200'}
                                    bg={isDragOver ? 'blue.50' : 'white'}
                                    p={6}
                                    display="block"
                                    cursor="pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDragLeave={() => setIsDragOver(false)}
                                    onDrop={handleDrop}
                                >
                                    <Input
                                        ref={fileInputRef}
                                        id={fileInputId}
                                        type="file"
                                        display="none"
                                        accept=".csv,text/csv,text/tab-separated-values"
                                        onChange={handleFileChange}
                                    />
                                    <Stack spacing={3} align="center">
                                        <Button
                                            colorScheme="blue"
                                            leftIcon={<IconUpload />}
                                            isLoading={isParsing}
                                            pointerEvents="none"
                                        >
                                            Select CSV file
                                        </Button>
                                        <Text fontSize="sm" color="gray.500">
                                            Drag and drop a CSV file here, or click to browse.
                                        </Text>
                                        <Text fontSize="sm" fontWeight="medium" color={importFileName ? 'gray.700' : 'gray.400'}>
                                            {importFileName || 'No file selected'}
                                        </Text>
                                    </Stack>
                                </Box>
                            )}

                            {importStep === 'map' && parseResult && (
                                <Stack spacing={5}>
                                    <Alert status="info">
                                        <AlertIcon />
                                        <AlertDescription>
                                            Parsed {parseResult.rowCount} rows from {importFileName || 'the selected file'}. Review the column mappings before importing.
                                        </AlertDescription>
                                    </Alert>
                                    <Stack spacing={4}>
                                        <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                                            <Box
                                                display="grid"
                                                gridTemplateColumns={{ base: '1fr', md: 'minmax(0, 220px) minmax(0, 1fr) minmax(0, 1fr)' }}
                                                gap={3}
                                                px={4}
                                                py={3}
                                                bg="gray.50"
                                                fontWeight="semibold"
                                            >
                                                <Text>Import Field</Text>
                                                <Text>Source Field</Text>
                                                <Text>Preview</Text>
                                            </Box>
                                            {importFields.map((field) => (
                                                <Box
                                                    key={field.key}
                                                    display="grid"
                                                    gridTemplateColumns={{ base: '1fr', md: 'minmax(0, 220px) minmax(0, 1fr) minmax(0, 1fr)' }}
                                                    gap={3}
                                                    px={4}
                                                    py={3}
                                                    borderTopWidth="1px"
                                                    alignItems="center"
                                                >
                                                    <Box>
                                                        <Text fontWeight="medium">{field.label}</Text>
                                                        {field.required && (
                                                            <Text fontSize="xs" color="gray.500">
                                                                required
                                                            </Text>
                                                        )}
                                                    </Box>
                                                    <FormControl isRequired={field.required}>
                                                        <Select
                                                            value={columnMapping[field.key] || ''}
                                                            onChange={(event) => handleMappingChange(field.key, event.target.value)}
                                                        >
                                                            <option value="">Not mapped</option>
                                                            {parseResult.columns.map((column: string) => (
                                                                <option value={column} key={column}>{column}</option>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <Text color={columnMapping[field.key] ? 'gray.700' : 'gray.400'}>
                                                        {columnMapping[field.key]
                                                            ? String(previewRow?.[columnMapping[field.key]] ?? '') || ''
                                                            : ''}
                                                    </Text>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Stack>
                                </Stack>
                            )}

                            {importStep === 'result' && importResult && (
                                <Stack spacing={4}>
                                    <Alert status={importResult.failed ? 'warning' : 'success'}>
                                        <AlertIcon />
                                        <AlertDescription>
                                            Imported {importResult.imported} products. {importResult.failed} rows failed.
                                        </AlertDescription>
                                    </Alert>
                                    {importResult.errors?.length > 0 && (
                                        <Box maxH="240px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                                            {importResult.errors.map((error: string) => (
                                                <Text key={error} fontSize="sm" color="red.600">{error}</Text>
                                            ))}
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        {importStep === 'upload' && (
                            <Button
                                colorScheme="blue"
                                onClick={() => setImportStep('map')}
                                isDisabled={!canMapFields}
                            >
                                Map Fields
                            </Button>
                        )}
                        {importStep === 'map' && (
                            <Button mr={3} onClick={() => setImportStep('upload')} variant="ghost">
                                Back
                            </Button>
                        )}
                        {importStep === 'map' && (
                            <Button colorScheme="blue" onClick={handleImport} isLoading={isImporting} isDisabled={!canImport}>
                                Import {parseResult?.rowCount || 0} Rows
                            </Button>
                        )}
                        {importStep === 'result' && (
                            <Button onClick={resetImport} variant="outline">
                                Import Another File
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </MainSection>
    );
};
